import { useCallback, useEffect, useState } from "react";
import TableView from "../components/TableView";
import RetatilerdataTableAction from "../generatedTableActions/RetatilerdataTableAction";
import Button from "../components/Button";
import Badge from "../components/Badge";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setRetatilerdata,
  setSearchTerm,
  setRerender,
  setTotalPage
} from "../generatedSlice/retatilerDataSlice";
import useSearchApi from "../customHooks/useSearchApi";
import { getRetatilerdata } from "../generatedRequest/retatilerData";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import RetatilerdataList from "../generatedTableList/RetatilerdataList";
import { TTableProps } from "../types/TableView";
import TableLoader from "../utils/Loaders/tableLoader";
import { useNavigate } from "react-router-dom";
import { getRetailerMappedAccessories } from "../generatedRequest/retailer";
import UploadResellerAccessoryFormModal from "../generatedFormModal/UploadResellerAccessoryFormModal";

const columns = [
  {
    name: "name",
    renderHeader: (rows: any) => (
      <>
        <b>Name</b>
      </>
    ),
    renderCell: (row: any) => (
      <>{row?.retailerData?.accessory?.accessoryName}</>
    ),
  },

  {
    name: "ModelNumber",
    renderHeader: (rows: any) => (
      <>
        <b>ModelNumber</b>
      </>
    ),
    renderCell: (row: any) => (
      <>{row?.retailerData?.accessory?.modelNumber}</>
    ),
  },

  {
    name: "sku",
    renderHeader: (rows: any) => (
      <>
        <b>SKU</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.retailerData?.accessory?.sku}</>,
  },
  {
    name: "brand",
    renderHeader: (rows: any) => (
      <>
        <b>Brand</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.retailerData?.accessory?.brand}</>,
  },
  // {
  //   name: "created",
  //   renderHeader: (rows: any) => (
  //     <>
  //       <b>Created At</b>
  //     </>
  //   ),
  //   renderCell: (row: any) => <>{row?.createdAt}</>,
  // },
  {
    name: "modified",
    renderHeader: (rows: any) => (
      <>
        <b>Modified At</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.updatedAt}</>,
  },

  {
    name: "status",
    renderHeader: (rows: any) => (
      <>
        <b>Status</b>
      </>
    ),
    renderCell: (row: any) => (
      <Badge
        variant={row?.statusForRetailer == "active" ? "success" : "danger"}
        className="fs-7 fw-bolder"
      >
        {row?.statusForRetailer}
      </Badge>
    ),
  },
  {
    name: "Actions",
    renderHeader: (rows: any) => (
      <>
        <b>Actions</b>
      </>
    ),
    renderCell: (row: any) => <RetatilerdataTableAction retailer={row} />,
  },
];

const RetatilerdataPage = ({
  retailerId,
  regionId,
}: {
  retailerId: string;
  regionId: string;
}) => {
  const dispatch = useDispatch();
  const [uploadExcel, setUploadExcel] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleUpload = useCallback(() => {
    setUploadExcel(!uploadExcel);
  }, [uploadExcel]);

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    sortBy,
    sortColumn,
    rerender1,
  } = useSelector((state: RootState) => state.retatilerData);

  const [initload, setInitload] = useState(true);


  const fetchData = useCallback(async (
    page: number = currentPage,
    limit: number = perPage,
    search: string = searchTerm
  ) => {
    if (!retailerId || !regionId) return;

    try {
      dispatch(setLoading(true));
      setSearchLoading(true);

      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        retailerRegionId: regionId,
        ...(search && { search: search })
      });

      const query = `${retailerId}?${queryParams.toString()}`;
      
      const response = await getRetailerMappedAccessories(query);
      
      if (response.data.status) {
        const totalPages = Math.ceil(response.data.data.totalCount / limit);
        dispatch(setRetatilerdata(response?.data?.data?.data));
        dispatch(setTotalPage(totalPages));
        // console.log("Total pages:", totalPages);
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      // console.error("API Error:", error);
      toast.error("Network Error");
    } finally {
      dispatch(setLoading(false));
      setSearchLoading(false);
      setInitload(false);
    }
  }, [dispatch, retailerId, regionId, currentPage, perPage, searchTerm]);

 
  useEffect(() => {
    if (retailerId && regionId) {
      fetchData();
    }
  }, [rerender1, retailerId, regionId]); 

  const handlePerPageChange = useCallback(
    (value: number) => {
      dispatch(setPerPage(value));
      dispatch(setCurrentPage(1)); 
      fetchData(1, value, searchTerm);
    },
    [dispatch, searchTerm, fetchData]
  );

  const handlePageChange = useCallback(
    (value: number) => {
      dispatch(setCurrentPage(value));
      fetchData(value, perPage, searchTerm);
    },
    [dispatch, perPage, searchTerm, fetchData]
  );

  const handleSearchParams = useCallback(
    (value: string) => {
      dispatch(setCurrentPage(1));
      dispatch(setSearchTerm(value));
      fetchData(1, perPage, value);
    },
    [dispatch, perPage, fetchData]
  );

  // console.log("Current state:", { perPage, currentPage, totalPage, searchTerm });

  const configuration: TTableProps = {
    columns: columns,
    rows: data,
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage,
    },
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Retailer Accessory",
  };

  const navigate = useNavigate();

  if (loading && initload) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h2>Retailers Accessory Datas</h2>
        <Button
          variant="primary"
          size="medium"
          handleButtonClick={() => handleUpload()}
        >
          Add Retatilerdata
        </Button>
      </div>
      <RetatilerdataList {...configuration} />
      {searchLoading && (
        <TableLoader isLoading={searchLoading} loadingMessage={"Loading..."} />
      )}
      {uploadExcel && (
        <UploadResellerAccessoryFormModal retailerId={retailerId} onClose={() => handleUpload()} fetchData={fetchData} />
      )}
    </>
  );
};

export default RetatilerdataPage;